import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { PRACTICE_GROUPS } from "@/data/practice";
import { CLINIC, SITE_URL } from "@/data/clinic";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const TITLE = "Conditions & Concerns We Work With | Agastya Chiro, Kurnool";
const DESC =
    "Personalized care in Kurnool for people experiencing neck pain, back pain, sciatica, joint, knee and shoulder discomfort — plus Ancient Marma Chikitsa, massage, cupping and scraping. Supportive, honest wellness care.";

const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Conditions & Concerns We Work With", item: `${SITE_URL}/conditions-and-concerns` },
    ],
};

export default function ConditionsPage() {
    return (
        <div className="bg-cream text-ink" data-testid="conditions-page">
            <Seo title={TITLE} description={DESC} path="/conditions-and-concerns" jsonLd={jsonLd} />
            <Header />
            <main className="mx-auto max-w-4xl px-4 pb-24 pt-36 sm:px-6 md:pt-44">
                <Reveal>
                    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-moss">
                        <Link to="/" className="transition-colors duration-300 hover:text-forest" data-testid="breadcrumb-home">
                            Home
                        </Link>
                        <span aria-hidden="true">/</span>
                        <span className="text-terracotta">Concerns</span>
                    </nav>
                    <h1
                        className="mt-6 font-serif text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl"
                        data-testid="conditions-heading"
                    >
                        Conditions & Concerns We Work With
                    </h1>
                    <p className="mt-6 max-w-2xl border-l-2 border-gold pl-5 font-serif text-lg italic leading-relaxed text-moss md:text-xl">
                        At Agastya Chiro and Ancient Marma Chikitsa Center, we take an ancient and
                        holistic approach to personalized care. Our practice focuses particularly on
                        spine, joint, mobility and musculoskeletal concerns, along with selected
                        wellness and traditional bodywork services.
                    </p>
                </Reveal>

                {PRACTICE_GROUPS.map((g) => (
                    <section key={g.id} id={g.id} className="mt-16 scroll-mt-28" data-testid={`conditions-group-${g.id}`}>
                        <Reveal>
                            <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">
                                {g.title}
                            </h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss md:text-base">{g.short}</p>
                        </Reveal>
                        <div className="mt-7 space-y-4">
                            {g.items.map((item, i) => (
                                <Reveal key={item.name} delay={i * 0.04}>
                                    <div className="rounded-2xl border border-sand bg-bone p-6" data-testid={`concern-${item.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                        <h3 className="font-serif text-xl font-medium text-ink">{item.name}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-moss">{item.text}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </section>
                ))}

                <Reveal>
                    <div className="mt-16 rounded-2xl border border-gold/30 bg-bone p-6" data-testid="conditions-safety-note">
                        <p className="text-sm leading-relaxed text-moss">
                            <span className="font-semibold text-ink">A note on medical care.</span> Our
                            work is a traditional, complementary wellness approach. It does not replace
                            medical diagnosis or treatment, and we do not claim to cure any condition.
                            For serious, persistent, sudden or worsening symptoms — including suspected
                            urinary infections or breathing difficulties — please seek appropriate
                            medical evaluation first, and keep your physician involved in your care.
                        </p>
                    </div>
                </Reveal>

                <Reveal>
                    <div className="mt-12 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-forest p-8 text-cream md:p-10" data-testid="conditions-cta">
                        <div>
                            <h2 className="font-serif text-2xl font-medium md:text-3xl">
                                Not sure if we can help?
                            </h2>
                            <p className="mt-2 text-sm text-cream/80">
                                Call us and describe what you are experiencing — an honest conversation
                                costs nothing. {CLINIC.hours.weekdays}.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href={CLINIC.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="conditions-book-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold"
                            >
                                Book a Consultation
                            </a>
                            <a
                                href={CLINIC.phoneTel}
                                data-testid="conditions-call-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-cream/50 px-6 py-3 text-sm font-semibold text-cream transition-colors duration-300 hover:border-cream"
                            >
                                <Phone className="h-4 w-4" /> Call {CLINIC.phoneDisplay}
                            </a>
                        </div>
                    </div>
                </Reveal>
            </main>
            <Footer />
        </div>
    );
}
